/**
 * Size & Fit dialog — event-delegated so bindings survive variant-picker morphs.
 */
(function () {
  /** @type {Map<string, Promise<string>>} */
  const sectionCache = new Map();
  const UNITS = ['inches', 'cm'];

  /** @type {Promise<boolean> | null} */
  let usizyScriptPromise = null;

  /**
   * Loads the Usizy fit-widget script once, memoized so repeat callers (hover
   * warm-up, drawer open, etc.) all share the same in-flight/settled request.
   * The widget div is server-rendered in variant-main-picker.liquid rather
   * than arriving with the fetched size-guide section, so the script can find
   * and initialize it as soon as it's requested — it doesn't have to wait on
   * that section fetch.
   *
   * Resolves `true` once the script has loaded (not once Usizy has finished
   * mounting its widget — that's what autoOpenUsizyWidget's own poll is for),
   * or `false` if the script failed to load.
   *
   * @returns {Promise<boolean>}
   */
  function loadUsizyScript() {
    if (usizyScriptPromise) return usizyScriptPromise;

    if (document.querySelector('script[src*="static.usizy.es"]')) {
      usizyScriptPromise = Promise.resolve(true);
      return usizyScriptPromise;
    }

    usizyScriptPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://static.usizy.es/js/platform.min.js';
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
    return usizyScriptPromise;
  }

  /**
   * Skips Usizy's own inline "Need fit help?" trigger link — clicking
   * straight through to its full experience the moment the Fit tab is
   * opened, so visitors only ever see the widget already in its open state,
   * never the small trigger button.
   *
   * Usizy's <usizy-adviser> web component (and the "Need fit help?" trigger
   * inside its shadow root) don't exist synchronously — loadUsizyScript()
   * above only just requested the script, and it chain-loads more scripts
   * before rendering anything. Polls rather than using a MutationObserver:
   * the trigger renders inside the component's shadow root asynchronously,
   * with no further light-DOM mutation on the panel to react to once the
   * (still-empty) <usizy-adviser> element itself first appears, so a plain
   * light-DOM observer can permanently miss the moment its shadow content
   * actually renders.
   *
   * @param {HTMLElement} dialog
   */
  async function autoOpenUsizyWidget(dialog) {
    const panel = dialog.querySelector('.size-fit__fit-guide');
    if (!(panel instanceof HTMLElement)) return;

    const lightbox = document.getElementById('usizy-lightbox');
    if (lightbox && lightbox.style.display !== 'none') return; // already open

    // Wait for the script itself to be ready before polling for its rendered
    // shadow-root content — no point sampling every 50ms while the (deferred,
    // chain-loading) script hasn't even executed yet. If it failed to load,
    // there's nothing to poll for.
    const loaded = await loadUsizyScript();
    if (!dialog.hasAttribute('open')) return;
    if (!loaded) {
      showUsizyUnavailable(dialog, panel, 'error');
      return;
    }

    const deadline = Date.now() + 15000;
    let clicked = false;

    const tick = () => {
      // Abandon if the drawer was closed before the widget was ready — the
      // trigger would still work, but it'd pop Usizy's own full-page popup
      // open with no drawer around it, out of nowhere.
      if (!dialog.hasAttribute('open')) return;

      const adviser = panel.querySelector('usizy-adviser');
      // Usizy's display-button response marks products it has no size chart
      // for with an `empty` attribute; no trigger will ever render for them.
      if (adviser?.hasAttribute('empty')) {
        showUsizyUnavailable(dialog, panel, 'empty');
        return;
      }
      const root = /** @type {any} */ (adviser)?.shadowRoot;
      const btn = root?.querySelector('#info-window');
      // Not `instanceof HTMLElement`: this element belongs to the custom
      // element's own realm, which has its own distinct HTMLElement
      // constructor — see the equivalent note on closeUsizyPopup.
      if (!clicked && btn && typeof (/** @type {any} */ (btn).click) === 'function') {
        /** @type {any} */ (btn).click();
        clicked = true;
      }
      // Keep watching after the click: the trigger can render while Usizy's
      // app bundle (media.usizy.es) is blocked, and then the popup never opens.
      const lightbox = document.getElementById('usizy-lightbox');
      if (clicked && lightbox && lightbox.style.display !== 'none') return;
      if (Date.now() < deadline) {
        setTimeout(tick, 50);
      } else {
        // Blocked or failed Usizy request (content blocker, tracking
        // protection, network): nothing else will ever clear the spinner.
        showUsizyUnavailable(dialog, panel, 'error');
      }
    };

    tick();
  }

  /**
   * Replaces the Fit tab's spinner with a message and a way back to the size
   * chart, for when Usizy will never embed: no size chart for this product
   * ('empty'), or its scripts/API never answered ('error'). Cleared on each
   * drawer open alongside usizyReady.
   *
   * @param {HTMLElement} dialog
   * @param {HTMLElement} panel
   * @param {'empty' | 'error'} reason
   */
  function showUsizyUnavailable(dialog, panel, reason) {
    if (dialog.dataset.usizyReady === 'true') return;
    dialog.dataset.usizyUnavailable = reason;

    let notice = panel.querySelector('.size-fit__fit-guide__unavailable');
    if (!(notice instanceof HTMLElement)) {
      notice = document.createElement('div');
      notice.className = 'size-fit__fit-guide__unavailable';
      notice.setAttribute('role', 'status');
      const message = document.createElement('p');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-fit__fit-guide__unavailable-link';
      button.textContent = panel.dataset.unavailableLink || 'View the size guide';
      button.addEventListener('click', () => {
        setActiveTab(dialog, 'size', { focusTab: true });
      });
      notice.append(message, button);
      panel.prepend(notice);
    }

    const message = notice.querySelector('p');
    if (message) {
      message.textContent =
        reason === 'empty'
          ? panel.dataset.unavailableEmpty || 'The Fit Guide isn’t available for this product.'
          : panel.dataset.unavailableError || 'The Fit Guide couldn’t load. Please try again later.';
    }
  }

  /**
   * Keeps --size-guide-header-height (consumed by the #usizy-lightbox rules in
   * product-size-guide.css) matched to the currently-open dialog's real header
   * height. Unlike desktop, the mobile header isn't a fixed height — it stacks
   * the close button above the tabs and wraps — so this can't be a static CSS
   * value; it has to track whatever the header actually renders at.
   */
  function updateSizeGuideHeaderHeightVar() {
    const dialog = document.querySelector('dialog[data-size-guide-dialog][open]');
    const header = dialog?.querySelector('.product__size-guide-dialog__header');
    if (!(header instanceof HTMLElement)) return;

    document.documentElement.style.setProperty(
      '--size-guide-header-height',
      `${header.getBoundingClientRect().height}px`
    );
  }

  // `resize`/`orientationchange`/visualViewport `resize` cover viewport-driven
  // header reflows (e.g. iOS Safari's collapsing URL bar, which doesn't
  // reliably fire a plain `window resize`). Neither fires for a same-viewport
  // reflow of the header itself — e.g. a webfont swapping in after the
  // initial (fallback-font) measurement, which real devices hit far more
  // often than a warm dev machine — so the header is also watched directly.
  window.addEventListener('resize', updateSizeGuideHeaderHeightVar);
  window.addEventListener('orientationchange', updateSizeGuideHeaderHeightVar);
  window.visualViewport?.addEventListener('resize', updateSizeGuideHeaderHeightVar);

  /** @type {ResizeObserver | null} */
  let sizeGuideHeaderObserver = null;

  /**
   * @param {HTMLElement} dialog
   */
  function observeSizeGuideHeader(dialog) {
    const header = dialog.querySelector('.product__size-guide-dialog__header');
    if (!(header instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

    if (!sizeGuideHeaderObserver) {
      sizeGuideHeaderObserver = new ResizeObserver(updateSizeGuideHeaderHeightVar);
    }
    sizeGuideHeaderObserver.disconnect();
    sizeGuideHeaderObserver.observe(header);
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  function loadSection(url) {
    // Cache the in-flight promise, not just the resolved HTML — warmSizeGuide
    // kicks this off on hover/focus, and the click that follows shortly after
    // should reuse that same request instead of firing a second one.
    const cached = sectionCache.get(url);
    if (cached) return cached;

    const promise = (async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })();

    sectionCache.set(url, promise);
    return promise;
  }

  /**
   * @param {string} html
   * @returns {string}
   */
  function unwrapSectionHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('.shopify-section');
    return section ? section.innerHTML : html;
  }

  /**
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function shouldConvertUnits(container) {
    const flag =
      container.dataset.convertUnits ||
      container.querySelector('[data-convert-units]')?.getAttribute('data-convert-units');
    return flag !== 'false';
  }

  /**
   * @param {HTMLElement} container
   * @param {string} newUnit
   */
  function updateUnit(container, newUnit) {
    if (!UNITS.includes(newUnit) || !shouldConvertUnits(container)) return;

    container.dataset.unit = newUnit;

    container.querySelectorAll('[data-ref="sizeFitTable"] td').forEach((el) => {
      const row = el.parentElement;
      if (!(row instanceof HTMLTableRowElement) || row.rowIndex === 0 || el.cellIndex === 0) return;

      if (!el.dataset.originalText) {
        el.dataset.originalText = el.textContent.trim();
      }

      const original = el.dataset.originalText;
      const matches = original.match(/(\d+\.?\d*)/g);
      if (!matches) return;

      if (newUnit === 'inches') {
        el.textContent = original;
        return;
      }

      const cm = matches.map((match) => Math.round(Number(match) * 2.54 * 10) / 10);
      el.textContent = `${cm.join(' - ')}cm`;
    });
  }

  /**
   * When Size is hidden, Fit stays blank.
   * Force panels to be direct children of the container before tab wiring.
   * @param {HTMLElement} contentEl
   */
  function normalizeTabPanels(contentEl) {
    const container =
      contentEl.querySelector('.size-fit-container') || contentEl;
    if (!(container instanceof HTMLElement)) return;

    /** @type {HTMLElement[]} */
    const panels = Array.from(contentEl.querySelectorAll('[data-size-guide-panel]')).filter(
      (panel) => panel instanceof HTMLElement
    );

    panels.forEach((panel) => {
      if (panel.parentElement !== container) {
        container.appendChild(panel);
      }
    });
  }

  /**
   * Wire injected panels to tab aria-controls / labelledby.
   * @param {HTMLElement} dialog
   * @param {HTMLElement} contentEl
   */
  function wireTabPanels(dialog, contentEl) {
    normalizeTabPanels(contentEl);

    contentEl.querySelectorAll('[data-size-guide-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const name = panel.dataset.sizeGuidePanel;
      const tab = dialog.querySelector(`[data-size-guide-tab="${name}"]`);
      if (!(tab instanceof HTMLElement)) return;

      const panelId = tab.getAttribute('aria-controls') || `SizeGuidePanel-${name}`;
      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      tab.setAttribute('aria-controls', panelId);
    });
  }

  /**
   * @param {HTMLElement} dialog
   * @param {string} tabName
   * @param {{ focusTab?: boolean }} [options]
   */
  function setActiveTab(dialog, tabName, options = {}) {
    const tabs = Array.from(dialog.querySelectorAll('[data-size-guide-tab]')).filter(
      (tab) => tab instanceof HTMLElement && !tab.hidden
    );

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-size-guide-tab') === tabName;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive && options.focusTab) tab.focus();
    });

    // Dialog-scoped, not content-scoped: the fit panel is server-rendered as a
    // sibling of [data-size-guide-content] (see variant-main-picker.liquid),
    // not fetched with the rest of the size-guide section, so it lives
    // outside contentEl's subtree.
    dialog.querySelectorAll('[data-size-guide-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.getAttribute('data-size-guide-panel') === tabName;
      if (isActive) {
        panel.hidden = false;
        panel.removeAttribute('hidden');
        panel.style.removeProperty('display');
      } else {
        panel.hidden = true;
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Show Fit Guide tab only when there's a fit panel to show it for. The fit
   * panel is server-rendered as a sibling of [data-size-guide-content] (see
   * variant-main-picker.liquid) rather than arriving with the fetched
   * section, so this no longer needs the section fetch to have completed —
   * it can run as soon as the dialog exists.
   * @param {HTMLElement} dialog
   */
  function syncFitTabVisibility(dialog) {
    const fitTab = dialog.querySelector('[data-size-guide-tab="fit"]');
    if (!(fitTab instanceof HTMLElement)) return;

    const hasFitContent = dialog.querySelector('[data-fit-guide-panel]') != null;

    fitTab.hidden = !hasFitContent;
    if (!hasFitContent && fitTab.classList.contains('is-active')) {
      setActiveTab(dialog, 'size');
    }
  }

  /**
   * @param {HTMLElement} contentEl
   * @param {HTMLElement} dialog
   */
  async function loadContent(contentEl, dialog) {
    if (contentEl.dataset.loaded === 'true') {
      wireTabPanels(dialog, contentEl);
      return;
    }

    const url = contentEl.dataset.url;
    if (!url) return;

    contentEl.setAttribute('aria-busy', 'true');

    try {
      const html = await loadSection(url);
      contentEl.innerHTML = unwrapSectionHtml(html);
      contentEl.dataset.loaded = 'true';

      if (shouldConvertUnits(contentEl)) {
        const initialUnit = contentEl.dataset.unit || 'cm';
        updateUnit(contentEl, initialUnit);
      }

      wireTabPanels(dialog, contentEl);
      syncFitTabVisibility(dialog);
      setActiveTab(dialog, 'size');
    } catch (error) {
      sectionCache.delete(url);
      delete contentEl.dataset.loaded;
      const message =
        contentEl.dataset.errorMessage || 'Unable to load size guide. Please try again.';
      const errorEl = document.createElement('p');
      errorEl.setAttribute('role', 'alert');
      errorEl.textContent = message;
      contentEl.replaceChildren(errorEl);
      console.warn('Error fetching size guide:', error);
    } finally {
      contentEl.removeAttribute('aria-busy');
    }
  }

  /**
   * Move the drawer out of any ancestor that traps it in a stacking context.
   *
   * The drawer is authored inside the PDP's `.product-details` column, which on
   * desktop is `position: sticky; z-index: var(--layer-flat)` (see
   * `.sticky-content--desktop` in base.css). A sticky element *always*
   * establishes a stacking context — even at `z-index: auto` — so every z-index
   * inside it, including this drawer's own `z-index: 100`, is scoped to that
   * context's single slot at layer 1. That sits below the theme's sticky header
   * (`#header-component`, `--layer-overlay: 16`) and below the PDP sticky
   * add-to-cart bar (`--layer-sticky - 1`, i.e. 7).
   *
   * While the drawer is modal none of that shows: showModal() puts it in the
   * top layer, which ignores stacking contexts entirely. But the Fit tab
   * deliberately demotes it to the non-modal show() so Usizy's own popup can
   * render over it (see transitionDialog below), and at that moment the drawer
   * drops back into ordinary stacking — trapped at layer 1, painted under the
   * header, with its own close button not even clickable.
   *
   * Raising the z-index cannot fix this: no value escapes an ancestor stacking
   * context. Moving the drawer out of `.product-details` can. `document.body`
   * is the right host on the PDP. When the drawer is rendered inside another
   * modal instead (the PLP quick-add dialog), that dialog is itself in the top
   * layer and paints its whole subtree above the header, so hoisting only as
   * far as that dialog keeps the drawer visible there too.
   *
   * @param {HTMLDialogElement} dialog
   */
  function hoistDialog(dialog) {
    const host = dialog.parentElement?.closest('dialog') ?? document.body;
    if (dialog.parentElement === host) return;

    // A section re-render can rebuild the authored copy while a previously
    // hoisted one is still parked on the host. getDialog() resolves by id and
    // getElementById returns the first match in tree order — the fresh,
    // authored copy — so drop the stale hoisted twin rather than leaving two
    // elements sharing an id.
    host.querySelectorAll(':scope > dialog[data-size-guide-dialog]').forEach((prev) => {
      if (prev !== dialog && prev.id && prev.id === dialog.id) prev.remove();
    });

    host.appendChild(dialog);
  }

  /**
   * Resolve the size-guide dialog for a control.
   * Dialog lives outside variant-picker (to survive morphs), so prefer aria-controls / id.
   * @param {Element | null} el
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(el) {
    if (!(el instanceof Element)) return null;

    const nested = el.closest('[data-size-guide-dialog]');
    if (nested instanceof HTMLDialogElement) return nested;

    const controlsId =
      el.getAttribute('aria-controls') || el.closest('[aria-controls]')?.getAttribute('aria-controls');
    if (controlsId) {
      const byId = document.getElementById(controlsId);
      if (byId instanceof HTMLDialogElement) return byId;
    }

    const root = el.closest('[data-size-guide]');
    const fromRoot = root?.querySelector('[data-size-guide-dialog]');
    return fromRoot instanceof HTMLDialogElement ? fromRoot : null;
  }

  /**
   * @param {HTMLElement} openBtn
   * @param {boolean} expanded
   */
  function setOpenButtonExpanded(openBtn, expanded) {
    openBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Starts warming the size-guide section fetch and the Usizy script on
   * intent (hover/focus/touch), well before the click that actually opens the
   * drawer. Both are memoized (loadSection's promise cache, loadUsizyScript's
   * usizyScriptPromise), so a click that follows shortly after just reuses
   * whatever this already kicked off, rather than starting over.
   *
   * Fire-and-forget: any failure here is swallowed. The real open flow
   * (loadContent / autoOpenUsizyWidget) re-requests and surfaces its own
   * error UI if this warm-up didn't pan out.
   *
   * @param {HTMLElement} openBtn
   */
  function warmSizeGuide(openBtn) {
    if (openBtn.dataset.sizeGuideWarmed === 'true') return;
    openBtn.dataset.sizeGuideWarmed = 'true';

    const dialog = getDialog(openBtn);
    const content = dialog?.querySelector('[data-size-guide-content]');
    const url = content instanceof HTMLElement ? content.dataset.url : undefined;
    if (url) loadSection(url).catch(() => {});

    loadUsizyScript();
  }

  /** @param {Event} event */
  function warmSizeGuideFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-size-guide-open]');
    if (openBtn instanceof HTMLElement) warmSizeGuide(openBtn);
  }

  // pointerover/focusin (not pointerenter/focus) because those don't bubble,
  // and every trigger button needs its own dedicated listener without one.
  document.addEventListener('pointerover', warmSizeGuideFromEvent);
  document.addEventListener('focusin', warmSizeGuideFromEvent);
  document.addEventListener('touchstart', warmSizeGuideFromEvent, { passive: true });

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-size-guide-open]');
    if (openBtn instanceof HTMLElement) {
      const dialog = getDialog(openBtn);
      const content = dialog?.querySelector('[data-size-guide-content]');
      if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement)) return;

      event.preventDefault();

      const openedByPointer = event instanceof MouseEvent || event instanceof PointerEvent;
      const onClose = () => {
        // Skip: this 'close' came from our own modal<->non-modal swap while the
        // Usizy widget is open (see watchUsizyLightbox), not a real user close.
        // dialog.close()'s 'close' event is a queued task, not synchronous, so
        // the flag can only be safely consumed here — not right after the
        // close()/reopen() call that set it.
        if (dialog.dataset.usizyTransition === 'true') {
          delete dialog.dataset.usizyTransition;
          return;
        }

        setOpenButtonExpanded(openBtn, false);
        dialog.removeEventListener('close', onClose);
        dialog.removeAttribute('data-pointer-open');
        sizeGuideHeaderObserver?.disconnect();
        // Closing the drawer should close Usizy's popup along with it, not leave
        // it open behind the drawer. The lightbox's own style attribute isn't
        // what's changing here, so its MutationObserver won't fire on its own to
        // do this.
        document.documentElement.removeAttribute('data-usizy-inline');
        const lightbox = document.getElementById('usizy-lightbox');
        if (lightbox) {
          applyUsizyIframeOverrides(lightbox, false);
          closeUsizyPopup(lightbox);
        }
        // Restore focus for keyboard users only — avoids a mouse focus ring on the trigger.
        if (!openedByPointer) openBtn.focus({ preventScroll: true });
      };
      dialog.addEventListener('close', onClose);

      setOpenButtonExpanded(openBtn, true);
      if (openedByPointer) dialog.setAttribute('data-pointer-open', '');
      // Must happen before it opens: moving an open dialog is what would be
      // disruptive, and the Fit tab can demote it to non-modal at any point
      // after that (see hoistDialog).
      hoistDialog(dialog);
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      updateSizeGuideHeaderHeightVar();
      observeSizeGuideHeader(dialog);

      // Reset the "ever embedded" mark from any previous run of this same
      // dialog (see syncUsizyEmbedState) — otherwise a second open would
      // start already marked ready and never show our own loading spinner
      // even before the widget has embedded this time.
      delete dialog.dataset.usizyReady;
      delete dialog.dataset.usizyUnavailable;

      // Show the drawer immediately with Size active, rather than leaving it
      // blank until the section fetch below resolves — the fit panel is
      // server-rendered (see variant-main-picker.liquid), so both no longer
      // depend on that fetch. loadContent() below re-runs setActiveTab('size')
      // once the size panel's own content lands, which is a harmless no-op
      // here since the active tab hasn't changed since this call.
      syncFitTabVisibility(dialog);
      setActiveTab(dialog, 'size');

      // Preload and auto-trigger Usizy the moment the drawer opens,
      // regardless of which tab is active — isUsizyEmbedded() keeps it
      // hidden (behind the still-modal dialog) until the Fit tab is
      // actually shown, so by the time someone switches to it, the widget
      // is already open and just needs revealing, instead of visibly
      // loading from scratch at that point. Must come after showModal()
      // above — autoOpenUsizyWidget checks that the dialog is actually open.
      loadUsizyScript();
      autoOpenUsizyWidget(dialog);
      const preloadedLightbox = document.getElementById('usizy-lightbox');
      if (preloadedLightbox) syncUsizyEmbedState(preloadedLightbox);

      if (openedByPointer) {
        dialog.setAttribute('tabindex', '-1');
        dialog.focus({ preventScroll: true });
        const clearPointerFlag = () => dialog.removeAttribute('data-pointer-open');
        dialog.addEventListener('keydown', clearPointerFlag, { once: true });
      }

      // Runs last, after the drawer is already visible: warmSizeGuide may
      // already have this in flight from an earlier hover/focus, in which
      // case this just awaits that same shared promise (see loadSection).
      await loadContent(content, dialog);
      return;
    }

    const closeBtn = target.closest('[data-size-guide-close]');
    if (closeBtn) {
      const dialog = getDialog(closeBtn);
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    const tab = target.closest('[data-size-guide-tab]');
    if (tab instanceof HTMLElement && !tab.hidden) {
      const dialog = getDialog(tab);
      const tabName = tab.dataset.sizeGuideTab;
      if (dialog instanceof HTMLElement && tabName) {
        setActiveTab(dialog, tabName);
        // Usizy is preloaded/pre-triggered when the drawer opens (see
        // [data-size-guide-open] above); this just reveals or re-hides it
        // depending on whether Fit is now the visible tab.
        const lightbox = document.getElementById('usizy-lightbox');
        if (lightbox) syncUsizyEmbedState(lightbox);
      }
      return;
    }

    const dialog = target.closest('[data-size-guide-dialog]');
    if (dialog instanceof HTMLDialogElement && target === dialog) {
      const rect = dialog.getBoundingClientRect();
      const isBackdropClick =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (isBackdropClick) dialog.close();
    }
  });

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-size-guide-tab]');
    if (!(tab instanceof HTMLElement) || tab.hidden) return;

    const dialog = getDialog(tab);
    if (!(dialog instanceof HTMLElement)) return;

    const tabs = Array.from(dialog.querySelectorAll('[data-size-guide-tab]')).filter(
      (el) => el instanceof HTMLElement && !el.hidden
    );
    const index = tabs.indexOf(tab);
    if (index < 0) return;

    let nextIndex = index;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const nextTab = tabs[nextIndex];
    const tabName = nextTab?.getAttribute('data-size-guide-tab');
    if (tabName) {
      setActiveTab(dialog, tabName, { focusTab: true });
      // See the equivalent comment in the [data-size-guide-tab] click handler.
      const lightbox = document.getElementById('usizy-lightbox');
      if (lightbox) syncUsizyEmbedState(lightbox);
    }
  });

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches('[name^="sizeUnit"]')) return;

      const contentEl = target.closest('[data-size-guide-content]');
      if (!(contentEl instanceof HTMLElement)) return;

      event.stopPropagation();
      updateUnit(contentEl, target.value);
    },
    true
  );

  /**
   * Usizy's popup (#usizy-lightbox) is a fixed-position div appended to
   * document.body, and Usizy's own script manages that DOM position itself
   * (it does its own body.removeChild/re-append internally on the widget's
   * lifecycle events) — reparenting it into our dialog's subtree breaks that
   * bookkeeping and throws inside Usizy's script.
   *
   * Promoting it into the top layer via the Popover API doesn't help either:
   * confirmed live (via elementFromPoint) that Chrome keeps an open modal
   * <dialog> above any popover regardless of stacking/insertion order — by
   * design, a popover can never cover an active modal.
   *
   * The only way left to let it render above our drawer is to take the
   * drawer itself out of the top layer while the widget is open: swap it
   * from showModal() to the non-modal show(), then let CSS (see
   * product-size-guide.css) resize Usizy's popup from full-viewport to match
   * the drawer's own screen rect, so ordinary z-index stacking puts it on
   * top and it reads as part of the still-open drawer. The drawer loses its
   * native focus-trap/inert-background/Escape-to-close for as long as the
   * widget is open, regaining them once the widget is dismissed.
   */

  /**
   * @param {HTMLDialogElement} dialog
   * @param {() => void} reopen
   */
  function transitionDialog(dialog, reopen) {
    // The flag is consumed (and cleared) inside onClose's 'close' handler, not
    // here — close()'s 'close' event is a queued task, so clearing it
    // immediately after this synchronous call would already be too late.
    dialog.dataset.usizyTransition = 'true';
    dialog.close();
    reopen();
  }

  /**
   * Usizy's own iframe (#vuesizy-frame) turns out to be same-origin — Usizy
   * writes into it directly rather than loading a cross-origin URL — so its
   * internals are reachable and restyleable:
   *  - .q-dialog__backdrop is a semi-transparent black layer meant for
   *    Usizy's own full-viewport popup; embedded in our drawer it just tints
   *    our background wherever their centered card doesn't fully cover the
   *    box, so it's made transparent.
   *  - .dialog-close (their own close button — wrapped in a .close-container
   *    in Quasar's "desktop" render, but sitting bare in their .q-header in
   *    "mobile" render, so both selectors are targeted) and .brand-logo
   *    (their "RUDSAK" wordmark) duplicate chrome our drawer already
   *    provides (its own close button, and the fact the whole drawer is
   *    already Rudsak-branded), so both are hidden any time our drawer is
   *    open —
   *    not only once `shouldEmbed` is true. `shouldEmbed` can lag or
   *    misfire for a moment after the drawer opens (e.g. before the Fit tab
   *    is first shown, or if the header-height measurement used for the
   *    embed positioning is briefly stale on a given device), and during
   *    that gap Usizy's own close button must not be the only visible one.
   *    It's only left showing once our drawer actually closes while the
   *    widget stays open, falling back to Usizy's own full-page popup (see
   *    watchUsizyLightbox) — their close button is then the sole way to
   *    dismiss it.
   *  - .privacy-link (a "Privacy" button in Usizy's own header, on the
   *    Height/Weight step) and .privacy-button (its equivalent in the
   *    results footer) are hidden per design — one line of chrome Usizy
   *    doesn't need duplicated, and removing it also shrinks Usizy's own
   *    header, giving the step content — and its Next button — more of the
   *    mobile drawer's limited vertical space to work with.
   *  - .q-stepper__step-inner (each wizard step's content wrapper) only has
   *    24px inline padding and none on top; bumped to match this drawer's
   *    own Size Guide tab padding (.size-fit) for a consistent look.
   *
   * The override rules live in <style> tags that are added/removed outright
   * (rather than toggling a persistent tag's `.disabled`) — Quasar rebuilds
   * this iframe's document between wizard steps anyway (handled by the
   * 'load' listener below), and toggling `.disabled` on a tag left in place
   * proved unreliable: something in Usizy's own re-rendering can silently
   * flip a stylesheet back to enabled with no call of ours involved.
   *
   * @param {HTMLElement} lightbox
   * @param {boolean} shouldEmbed
   */
  function applyUsizyIframeOverrides(lightbox, shouldEmbed) {
    const iframe = lightbox.querySelector('#vuesizy-frame');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    const drawerOpen = document.querySelector('dialog[data-size-guide-dialog][open]') != null;
    const hideNativeChrome = shouldEmbed || drawerOpen;

    try {
      const doc = iframe.contentDocument;
      if (!doc?.head) return;

      const existingCloseOverride = doc.getElementById('size-guide-usizy-close-override');
      if (hideNativeChrome) {
        if (!existingCloseOverride) {
          const closeStyle = doc.createElement('style');
          closeStyle.id = 'size-guide-usizy-close-override';
          closeStyle.textContent = `.close-container, .dialog-close, .brand-logo, .privacy-link, .privacy-button { display: none !important; }`;
          doc.head.appendChild(closeStyle);
        }
      } else {
        existingCloseOverride?.remove();
      }

      const existing = doc.getElementById('size-guide-usizy-overrides');
      if (!shouldEmbed) {
        existing?.remove();
        return;
      }
      if (existing) return;

      const style = doc.createElement('style');
      style.id = 'size-guide-usizy-overrides';
      style.textContent = `
        .q-dialog__backdrop { background: transparent !important; }
        /* Quasar itself already reserves ~64px above <main> for a header
           toolbar (now empty, since its logo/close button are hidden above),
           so only a little extra top padding is needed here on top of that. */
        .q-stepper__step-inner { padding: 8px 64px 24px !important; }
        @media (min-width: 768px) {
          /* Usizy's own .q-page wraps each step in a Quasar "flex-center"
             utility class, vertically centering short steps (e.g. the
             Height/Weight form) inside the full-height page instead of
             pinning them under the header. Force block flow so content
             stacks from the top regardless of which centering utility
             classes Usizy applies, then add breathing room below the
             reserved header area. */
          .q-page {
            display: block !important;
            padding-top: 24px !important;
          }
        }
        @media (max-width: 767px) {
          .q-stepper__step-inner { padding: 8px 20px 24px !important; }
          /* Usizy ships two different DOMs for this step and picks between
             them by user agent, not by width (verified 2026-09-15 against
             the theme-dev preview on a real phone UA vs a desktop UA at the
             same 390px viewport):
               - desktop UA: the photo wrapper is .limit-img, inside
                 .row.q-gutter-lg alongside the form at .col;
               - mobile UA: the photo wrapper is .image-wrapper, carrying
                 Quasar's own offset-4 col-4 (33% wide, offset by 33%),
                 inside .row.q-mt-neg-md with the form stacked below it.
             Both must be handled here: a narrowed desktop browser — how this
             drawer is usually reviewed — gets the .limit-img DOM at mobile
             widths, while a real phone gets .image-wrapper. Rules written for
             only one of them silently match nothing on the other.

             Design calls for the photo stacked above the form on mobile, so
             stack the desktop-DOM row (the mobile DOM already stacks) and
             size the wrapper down by width in both.

             Size by WIDTH only — never cap the height. Quasar's QImg builds
             its box from a sizer child with padding-bottom: 125% (a
             ratio-from-width trick) and the wrapper carries overflow: hidden,
             so a max-height on the wrapper does not scale the photo, it
             shrinks the visible window over an image already laid out at the
             full 125% height — cutting off the model's head and feet. That
             is what ClickUp 86ajqyqkt kept reporting. Note that measuring the
             img element alone does not reveal this: the crop lives in the
             parent's clipped window, so verify against the wrapper's rendered
             box, and by eye.

             112px width x 1.25 gives the ~140px tall photo the short-viewport
             layout needs, with nothing clipped because the box is genuinely
             smaller. max-width keeps it proportional on very narrow screens.

             Note for editors: do not use backticks anywhere inside this
             template literal (including inside CSS comments) — an unescaped
             backtick here terminates the JS string early and the remainder
             is parsed as code, which can silently throw and get swallowed by
             the surrounding try/catch, disabling every override below it. */
          .q-stepper__step-inner > .row.q-gutter-lg {
            flex-direction: column !important;
            margin-left: 0 !important;
            /* Quasar's gutter classes work by pulling the row up with a
               negative top margin and pushing each child back down. Stacking
               the row leaves that -24px in place, which slides the photo up
               under Usizy's own .q-header — still in the layout once its
               logo and close button are hidden above, and an opaque white
               bar at z-index 2000. The header then paints over the top of
               the photo, cutting off the model's head. Nothing about the
               photo's own box reveals this: the wrapper and the img both
               measure a correct 112x140 at the right aspect ratio while the
               head is missing on screen, so check it with a screenshot, not
               a measurement. */
            margin-top: 0 !important;
          }
          .q-stepper__step-inner .limit-img,
          .q-stepper__step-inner .image-wrapper {
            flex: 0 0 auto !important;
            width: 112px !important;
            max-width: 45% !important;
            max-height: none !important;
            margin: 0 auto 16px !important;
            padding-left: 0 !important;
          }
          /* Safety net only. Quasar sets object-fit: cover inline on its own
             img, which would crop whenever the wrapper's box ratio drifts
             from the photo's. It does not fix a clipping parent — see above. */
          .q-stepper__step-inner .limit-img img,
          .q-stepper__step-inner .image-wrapper img {
            object-fit: contain !important;
          }
          .q-stepper__step-inner > .row.q-gutter-lg > .col {
            flex: 1 1 auto !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          /* Trims the Height/Weight step title's default browser <p> margin
             to hand a little more vertical space back to the interactive
             fields below, given our own drawer header above and the model
             photo box already eat into mobile's limited height. */
          .q-stepper__step-inner p.row.col-12.justify-center {
            margin-bottom: 6px !important;
          }
        }
      `;
      doc.head.appendChild(style);
    } catch {
      // Cross-origin after all, or not ready yet — the 'load' listener below retries.
    }
  }

  /**
   * Closes Usizy's popup by clicking its own close button (rather than
   * setting #usizy-lightbox's style directly) so whatever internal
   * state/cleanup Usizy runs on close happens the same way it would for a
   * real user click.
   *
   * @param {HTMLElement} lightbox
   */
  function closeUsizyPopup(lightbox) {
    if (lightbox.style.display === 'none') return;

    const iframe = lightbox.querySelector('#vuesizy-frame');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    try {
      // Not `instanceof HTMLElement`: closeBtn belongs to the iframe's own
      // realm, which has its own distinct HTMLElement constructor — a
      // cross-realm instanceof check like that is always false here, even
      // for a perfectly valid element.
      const closeBtn = iframe.contentDocument?.querySelector('.dialog-close');
      if (closeBtn && typeof (/** @type {any} */ (closeBtn).click) === 'function') {
        /** @type {any} */ (closeBtn).click();
      }
    } catch {
      // Cross-origin after all — nothing we can do; the popup stays open.
    }
  }

  /**
   * True only once the Fit tab is actually the visible one — not merely
   * once its panel exists — so the widget can be pre-triggered the moment
   * the drawer opens (see autoOpenUsizyWidget's call sites) while any other
   * tab is showing without it becoming visible early — syncUsizyEmbedState
   * forces it invisible whenever this is false but the popup is still open,
   * since a modal dialog's own (translucent) backdrop only dims whatever's
   * behind it, it doesn't hide it — the popup would otherwise show through,
   * however faintly, whenever it isn't sized/positioned to exactly match
   * (and fully cover) the dialog's own opaque content.
   *
   * @param {HTMLElement} lightbox
   * @returns {boolean}
   */
  function isUsizyEmbedded(lightbox) {
    const isVisible = lightbox.style.display !== 'none';
    const dialog = document.querySelector('dialog[data-size-guide-dialog][open]');
    const fitPanel = dialog?.querySelector('.size-fit__fit-guide');
    return isVisible && fitPanel instanceof HTMLElement && !fitPanel.hidden;
  }

  /**
   * Applies (or reverts) the embedded look based on current state — the
   * Fit tab being visible AND Usizy's popup being open. Called both when
   * the popup's own visibility changes, and when the active tab changes
   * (switching tabs doesn't touch the popup's style, so nothing else would
   * notice a tab switch on its own).
   *
   * @param {HTMLElement} lightbox
   */
  function syncUsizyEmbedState(lightbox) {
    const isVisible = lightbox.style.display !== 'none';
    const shouldEmbed = isUsizyEmbedded(lightbox);
    const dialog = document.querySelector('dialog[data-size-guide-dialog][open]');

    updateSizeGuideHeaderHeightVar();
    document.documentElement.toggleAttribute('data-usizy-inline', shouldEmbed);
    document.documentElement.toggleAttribute('data-usizy-preloading', isVisible && !shouldEmbed);
    applyUsizyIframeOverrides(lightbox, shouldEmbed);

    if (!(dialog instanceof HTMLDialogElement)) return;

    // Mark this dialog's Usizy widget as having embedded at least once. Our
    // own spinner (.size-fit__fit-guide__loading) is otherwise hidden only
    // by html[data-usizy-inline] — but Usizy closes #usizy-lightbox itself
    // (isVisible flips false, dropping data-usizy-inline) once the quiz is
    // finished, not only when the widget never loaded. Without this mark,
    // finishing the quiz brings our own "still loading" spinner right back
    // over Usizy's own finished result, forever, instead of leaving it
    // hidden — see the CSS rule keyed off [data-usizy-ready] in
    // product-size-guide.css. Reset on each open (above), so a second run
    // still shows the spinner correctly until it embeds again.
    if (shouldEmbed) {
      dialog.dataset.usizyReady = 'true';
      // A late-arriving widget wins over a timed-out "unavailable" notice.
      delete dialog.dataset.usizyUnavailable;
    }

    if (shouldEmbed && dialog.matches(':modal')) {
      transitionDialog(dialog, () => dialog.show());
    } else if (!shouldEmbed && dialog.open && !dialog.matches(':modal')) {
      // Covers both the popup actually closing AND navigating away from the
      // Fit tab while it's still open in the background — either way,
      // going modal again is what hides it.
      transitionDialog(dialog, () => dialog.showModal());
    }
  }

  /**
   * A height saved in CM comes back from Usizy's open-popup call converted to
   * quarter inches ({ ft: 5, in: 5.75 } for 167 cm), and Usizy's "in" field
   * (mask "##", maxlength 2) drops the decimal point and truncates it to 57,
   * which then fails its own 0-11 check and disables Next (17tqamewut7).
   * Round those heights to whole inches before Usizy's app (an XHR client
   * inside this same-origin iframe) reads them — the same rounding its own
   * CM -> IN toggle applies.
   *
   * @param {HTMLIFrameElement} iframe
   */
  function roundUsizySavedHeights(iframe) {
    try {
      const xhr = /** @type {any} */ (iframe.contentWindow)?.XMLHttpRequest;
      if (typeof xhr?.prototype?.open !== 'function' || xhr.prototype.sizeGuideHeightFix) return;
      xhr.prototype.sizeGuideHeightFix = true;

      const open = xhr.prototype.open;
      xhr.prototype.open = function (/** @type {string} */ method, /** @type {string} */ url, ...rest) {
        try {
          if (/\/open-popup(?:[?#]|$)/.test(String(url))) {
            this.addEventListener('readystatechange', () => roundOpenPopupHeights(this));
          }
        } catch {
          // Leave the request untouched.
        }
        return open.call(this, method, url, ...rest);
      };
    } catch {
      // Iframe not reachable (or Usizy changed how it loads) — do nothing.
    }
  }

  /**
   * Rewrites a finished open-popup response only when it holds an imperial
   * height that is not a whole 0-11 inches; anything else Usizy sends —
   * another shape, another responseType, not JSON — is left exactly as is.
   *
   * @param {any} request
   */
  function roundOpenPopupHeights(request) {
    try {
      if (request.readyState !== 4 || (request.responseType !== '' && request.responseType !== 'text')) return;

      const body = JSON.parse(request.responseText);
      const profiles = body?.data?.imperial;
      if (!profiles || typeof profiles !== 'object') return;

      let changed = false;
      Object.values(profiles).forEach((profile) => {
        const height = profile?.height;
        if (!Number.isFinite(height?.ft) || !Number.isFinite(height?.in)) return;
        if (Number.isInteger(height.in) && height.in >= 0 && height.in <= 11) return;

        const inches = Math.round(height.ft * 12 + height.in);
        height.ft = Math.floor(inches / 12);
        height.in = inches % 12;
        changed = true;
      });
      if (!changed) return;

      const text = JSON.stringify(body);
      Object.defineProperty(request, 'responseText', { value: text });
      Object.defineProperty(request, 'response', { value: text });
    } catch {
      // Not the response we expect — leave it untouched.
    }
  }

  /**
   * @param {HTMLElement} lightbox
   */
  function watchUsizyLightbox(lightbox) {
    const iframe = lightbox.querySelector('#vuesizy-frame');
    if (iframe instanceof HTMLIFrameElement) roundUsizySavedHeights(iframe);
    // Quasar rebuilds the iframe's document between wizard steps, dropping our
    // injected <style> — reapply (with whatever the embed state is by then)
    // each time it reloads.
    iframe?.addEventListener('load', () => {
      if (iframe instanceof HTMLIFrameElement) roundUsizySavedHeights(iframe);
      applyUsizyIframeOverrides(lightbox, isUsizyEmbedded(lightbox));
    });

    new MutationObserver(() => syncUsizyEmbedState(lightbox)).observe(lightbox, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  new MutationObserver(() => {
    const lightbox = document.getElementById('usizy-lightbox');
    if (lightbox instanceof HTMLElement && !lightbox.dataset.sizeGuideWatched) {
      lightbox.dataset.sizeGuideWatched = 'true';
      watchUsizyLightbox(lightbox);
    }
  }).observe(document.body, { childList: true });
})();
